import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { analytics, EVENTS } from '../../lib/analytics'
import {
  Box, Typography, Button, Card, Stack, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, CircularProgress,
  Tabs, Tab, IconButton, Tooltip, Select, InputLabel, FormControl,
  Stepper, Step, StepLabel, Alert,
} from '@mui/material'
import {
  Add, Build, PlayArrow, CheckCircle, Close, UploadFile, Delete,
  BugReport, Category,
} from '@mui/icons-material'
import { useMaintenance, useCreateMaintenance } from '../../hooks/useFleet'
import { useDevices, Device } from '../../hooks/useDevices'
import {
  useCategories, useCreateCategory, useMaintenanceTypes, useCreateMaintenanceType,
  useServiceOrders, useCreateServiceOrder, useUpdateServiceOrderStatus,
  useServiceIssues, useCreateIssue, useResolveIssue,
  useUploadDocument, type ServiceOrder,
} from '../../hooks/useServiceHistory'

function unwrap<T>(d: unknown): T[] {
  if (Array.isArray(d)) return d as T[]
  const o = d as { devices?: T[] } | undefined
  return o?.devices ?? []
}

export default function MaintenancePage() {
  const { t } = useTranslation()
  const [tab, setTab] = useState(0)

  return (
    <Box>
      <Typography variant="h5" fontWeight={800} color="#0f172a" sx={{ letterSpacing: '-0.02em' }}>
        {t('maintenance.title')}
      </Typography>
      <Typography variant="body2" color="#94a3b8" sx={{ mt: 0.5, mb: 2 }}>
        {t('fleet.maintenance')}
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab icon={<Build sx={{ fontSize: 16 }} />} label={t('maintenance.schedulesConfig')} iconPosition="start" />
        <Tab icon={<PlayArrow sx={{ fontSize: 16 }} />} label={t('maintenance.serviceOrders')} iconPosition="start" />
      </Tabs>

      {tab === 0 && <SchedulesTab />}
      {tab === 1 && <ServiceOrdersTab />}
    </Box>
  )
}

// ═══════════════════════════════════════════════════════════════
// TAB 1: Maintenance Schedules + Categories/Types Config
// ═══════════════════════════════════════════════════════════════

function SchedulesTab() {
  const { t } = useTranslation()
  const { data: schedules = [], isLoading } = useMaintenance()
  const { data: devicesRaw } = useDevices()
  const devices = unwrap<Device>(devicesRaw)
  const { data: categories = [] } = useCategories()
  const { data: types = [] } = useMaintenanceTypes()
  const create = useCreateMaintenance()
  const createCat = useCreateCategory()
  const createType = useCreateMaintenanceType()

  const [scheduleDialog, setScheduleDialog] = useState(false)
  const [catDialog, setCatDialog] = useState(false)
  const [typeDialog, setTypeDialog] = useState(false)
  const [form, setForm] = useState({ device_id: '', task: '', every_km: '', every_days: '' })
  const [catForm, setCatForm] = useState({ name: '', description: '' })
  const [typeForm, setTypeForm] = useState({ name: '', description: '', category_id: '' })

  const deviceMap = new Map(devices.map((d) => [d.id, d.name]))

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Button variant="contained" startIcon={<Add />} onClick={() => setScheduleDialog(true)}
          sx={{ background: 'linear-gradient(135deg,#34A8E0,#2B8BB9)' }}>
          {t('maintenance.newSchedule')}
        </Button>
        <Button variant="outlined" startIcon={<Category />} onClick={() => setCatDialog(true)}>
          {t('maintenance.addCategory')}
        </Button>
        <Button variant="outlined" startIcon={<Build />} onClick={() => setTypeDialog(true)}>
          {t('maintenance.addType')}
        </Button>
      </Stack>

      {/* Categories & Types display */}
      {(categories.length > 0 || types.length > 0) && (
        <Card sx={{ p: 2, mb: 2, border: '1px solid #e2e8f0' }}>
          <Typography variant="caption" color="#64748b" fontWeight={600}>{t('maintenance.categoriesTypes')}</Typography>
          <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
            {categories.map((c) => (
              <Chip key={c.id} label={c.name} size="small" color="primary" variant="outlined" />
            ))}
            {types.map((t) => (
              <Chip key={t.id} label={t.name} size="small" variant="outlined" />
            ))}
          </Stack>
        </Card>
      )}

      {/* Schedules list */}
      {isLoading ? <CircularProgress sx={{ color: '#34A8E0' }} /> : (
        <Stack spacing={1.5}>
          {schedules.length === 0 && (
            <Card sx={{ p: 4, textAlign: 'center', border: '1px solid #e2e8f0' }}>
              <Build sx={{ fontSize: 40, color: '#cbd5e1', mb: 1 }} />
              <Typography color="#64748b">{t('maintenance.noSchedules')}</Typography>
            </Card>
          )}
          {schedules.map((s) => (
            <Card key={s.id} sx={{ p: 2, border: '1px solid #e2e8f0' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography fontWeight={600}>{s.task}</Typography>
                  <Typography variant="caption" color="#94a3b8">
                    {deviceMap.get(s.device_id) ?? s.device_id.slice(0, 8)}
                    {s.every_km && ` · ${s.every_km} ${t('maintenance.everyKm')}`}
                    {s.every_days && ` · ${s.every_days} ${t('maintenance.everyDays')}`}
                  </Typography>
                </Box>
                <Chip label={s.is_active ? 'Active' : 'Inactive'} size="small"
                  color={s.is_active ? 'success' : 'default'} variant="outlined" />
              </Stack>
            </Card>
          ))}
        </Stack>
      )}

      {/* New Schedule Dialog */}
      <Dialog open={scheduleDialog} onClose={() => setScheduleDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('fleet.createSchedule')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField select label={t('analytics.device')} value={form.device_id}
              onChange={(e) => setForm((p) => ({ ...p, device_id: e.target.value }))} fullWidth>
              {devices.map((d) => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
            </TextField>
            <TextField label={t('maintenance.task')} value={form.task}
              onChange={(e) => setForm((p) => ({ ...p, task: e.target.value }))} fullWidth
              placeholder="Oil Change, Tire Rotation, etc." />
            <Stack direction="row" spacing={2}>
              <TextField label={t('maintenance.everyKm')} type="number" value={form.every_km}
                onChange={(e) => setForm((p) => ({ ...p, every_km: e.target.value }))} fullWidth />
              <TextField label={t('maintenance.everyDays')} type="number" value={form.every_days}
                onChange={(e) => setForm((p) => ({ ...p, every_days: e.target.value }))} fullWidth />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleDialog(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" disabled={create.isPending} onClick={async () => {
            await create.mutateAsync({
              device_id: form.device_id, task: form.task,
              every_km: form.every_km ? Number(form.every_km) : null,
              every_days: form.every_days ? Number(form.every_days) : null,
            })
            analytics.track(EVENTS.MAINTENANCE_CREATED, { device_id: form.device_id, task: form.task })
            setScheduleDialog(false)
            setForm({ device_id: '', task: '', every_km: '', every_days: '' })
          }}>{t('common.create')}</Button>
        </DialogActions>
      </Dialog>

      {/* Category Dialog */}
      <Dialog open={catDialog} onClose={() => setCatDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('maintenance.addCategory')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label={t('common.name')} value={catForm.name} onChange={(e) => setCatForm((p) => ({ ...p, name: e.target.value }))} fullWidth />
            <TextField label={t('common.description')} value={catForm.description} onChange={(e) => setCatForm((p) => ({ ...p, description: e.target.value }))} fullWidth multiline rows={2} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCatDialog(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" disabled={createCat.isPending} onClick={async () => {
            try {
              await createCat.mutateAsync(catForm)
              setCatDialog(false)
              setCatForm({ name: '', description: '' })
            } catch { /* mutation error handled by react-query */ }
          }}>{t('common.create')}</Button>
        </DialogActions>
      </Dialog>

      {/* Type Dialog */}
      <Dialog open={typeDialog} onClose={() => setTypeDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('maintenance.addType')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField select label={t('maintenance.addCategory')} value={typeForm.category_id}
              onChange={(e) => setTypeForm((p) => ({ ...p, category_id: e.target.value }))} fullWidth>
              <MenuItem value="">None</MenuItem>
              {categories.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </TextField>
            <TextField label={t('common.name')} value={typeForm.name} onChange={(e) => setTypeForm((p) => ({ ...p, name: e.target.value }))} fullWidth />
            <TextField label={t('common.description')} value={typeForm.description} onChange={(e) => setTypeForm((p) => ({ ...p, description: e.target.value }))} fullWidth multiline rows={2} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTypeDialog(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" disabled={createType.isPending} onClick={async () => {
            try {
              await createType.mutateAsync({ ...typeForm, category_id: typeForm.category_id || undefined })
              setTypeDialog(false)
              setTypeForm({ name: '', description: '', category_id: '' })
            } catch { /* mutation error handled by react-query */ }
          }}>{t('common.create')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

// ═══════════════════════════════════════════════════════════════
// TAB 2: Service Orders (workflow: open → in_progress → completed → closed)
// ═══════════════════════════════════════════════════════════════

const STATUS_COLORS: Record<string, string> = {
  open: '#3b82f6', in_progress: '#f59e0b', completed: '#10b981', closed: '#64748b',
}
const PRIORITY_COLORS: Record<string, string> = {
  low: '#94a3b8', normal: '#3b82f6', high: '#f59e0b', urgent: '#ef4444',
}
const NEXT_STATUS: Record<string, string> = {
  open: 'in_progress', in_progress: 'completed', completed: 'closed',
}

function ServiceOrdersTab() {
  const { t } = useTranslation()
  const { data: devicesRaw } = useDevices()
  const devices = unwrap<Device>(devicesRaw)
  const { data: categories = [] } = useCategories()
  const { data: types = [] } = useMaintenanceTypes()
  const { data: orders = [], isLoading } = useServiceOrders()
  const { data: issues = [] } = useServiceIssues()
  const createOrder = useCreateServiceOrder()
  const updateStatus = useUpdateServiceOrderStatus()
  const createIssue = useCreateIssue()
  const resolveIssue = useResolveIssue()
  const uploadDoc = useUploadDocument()

  const [orderDialog, setOrderDialog] = useState(false)
  const [issueDialog, setIssueDialog] = useState(false)
  const [statusFilter, setStatusFilter] = useState('')
  const [orderForm, setOrderForm] = useState({
    device_id: '', title: '', description: '', priority: 'normal',
    service_type: '', category_id: '', type_id: '',
    odometer_km: '', cost: '', vendor_name: '', vendor_contact: '', notes: '',
  })
  const [issueForm, setIssueForm] = useState({ device_id: '', title: '', description: '', severity: 'medium' })

  const deviceMap = new Map(devices.map((d) => [d.id, d.name]))
  const filtered = statusFilter ? orders.filter((o) => o.status === statusFilter) : orders

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center">
        <Button variant="contained" startIcon={<Add />} onClick={() => setOrderDialog(true)}
          sx={{ background: 'linear-gradient(135deg,#34A8E0,#2B8BB9)' }}>
          {t('maintenance.newServiceOrder')}
        </Button>
        <Button variant="outlined" startIcon={<BugReport />} onClick={() => setIssueDialog(true)}>
          {t('maintenance.reportIssue')}
        </Button>
        <Box sx={{ flex: 1 }} />
        <Stack direction="row" spacing={0.5}>
          {['', 'open', 'in_progress', 'completed', 'closed'].map((s) => (
            <Chip key={s} label={s === '' ? t('common.all') : s === 'open' ? t('maintenance.open') : s === 'in_progress' ? t('maintenance.inProgress') : s === 'completed' ? t('maintenance.completed') : t('maintenance.closed')} size="small"
              variant={statusFilter === s ? 'filled' : 'outlined'}
              onClick={() => setStatusFilter(s)}
              sx={statusFilter === s ? { bgcolor: STATUS_COLORS[s] || '#34A8E0', color: '#fff' } : {}} />
          ))}
        </Stack>
      </Stack>

      {/* Open issues banner */}
      {issues.filter((i) => i.status === 'open').length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {issues.filter((i) => i.status === 'open').length} open issue(s) need attention.
          Create a service order to resolve them.
        </Alert>
      )}

      {isLoading ? <CircularProgress sx={{ color: '#34A8E0' }} /> : (
        <Stack spacing={1.5}>
          {filtered.length === 0 && (
            <Card sx={{ p: 4, textAlign: 'center', border: '1px solid #e2e8f0' }}>
              <PlayArrow sx={{ fontSize: 40, color: '#cbd5e1', mb: 1 }} />
              <Typography color="#64748b">{t('maintenance.noServiceOrders')}</Typography>
            </Card>
          )}
          {filtered.map((order) => (
            <Card key={order.id} sx={{ p: 2, border: '1px solid #e2e8f0', borderLeft: `4px solid ${STATUS_COLORS[order.status] ?? '#e2e8f0'}` }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box sx={{ flex: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <Typography fontWeight={700} color="#1e293b">{order.title}</Typography>
                    <Chip label={order.status.replace('_', ' ')} size="small"
                      sx={{ bgcolor: STATUS_COLORS[order.status], color: '#fff', fontSize: 10, fontWeight: 600 }} />
                    <Chip label={order.priority} size="small" variant="outlined"
                      sx={{ borderColor: PRIORITY_COLORS[order.priority], color: PRIORITY_COLORS[order.priority], fontSize: 10 }} />
                  </Stack>
                  <Typography variant="caption" color="#94a3b8">
                    {deviceMap.get(order.device_id) ?? order.device_id.slice(0, 8)}
                    {order.service_type && ` · ${order.service_type}`}
                    {order.vendor_name && ` · ${t('maintenance.vendor')}: ${order.vendor_name}`}
                    {order.cost != null && ` · ${t('maintenance.cost')}: $${order.cost.toFixed(2)}`}
                    {order.odometer_km != null && ` · ${order.odometer_km.toFixed(0)} km`}
                  </Typography>
                  {order.description && (
                    <Typography variant="body2" color="#64748b" sx={{ mt: 0.5 }}>{order.description}</Typography>
                  )}
                  <Typography variant="caption" color="#cbd5e1" sx={{ mt: 0.5, display: 'block' }}>
                    {t('maintenance.open')}: {new Date(order.opened_at).toLocaleString()}
                    {order.completed_at && ` · ${t('maintenance.completed')}: ${new Date(order.completed_at).toLocaleString()}`}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={0.5}>
                  {NEXT_STATUS[order.status] && (
                    <Tooltip title={`Move to ${NEXT_STATUS[order.status].replace('_', ' ')}`}>
                      <IconButton size="small" color="primary"
                        onClick={() => { updateStatus.mutate({ id: order.id, status: NEXT_STATUS[order.status] }); if (NEXT_STATUS[order.status] === 'completed' || NEXT_STATUS[order.status] === 'closed') { analytics.track(EVENTS.MAINTENANCE_COMPLETED, { order_id: order.id, device_id: order.device_id, status: NEXT_STATUS[order.status] }) } }}>
                        {order.status === 'completed' ? <Close fontSize="small" /> : <CheckCircle fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Upload document">
                    <IconButton size="small" component="label">
                      <UploadFile fontSize="small" />
                      <input type="file" hidden onChange={(e) => {
                        const f = e.target.files?.[0]
                        if (f) uploadDoc.mutate({ orderId: order.id, file: f })
                      }} />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>
            </Card>
          ))}
        </Stack>
      )}

      {/* New Service Order Dialog */}
      <Dialog open={orderDialog} onClose={() => setOrderDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('maintenance.newServiceOrder')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField select label={`${t('analytics.device')} *`} value={orderForm.device_id}
              onChange={(e) => setOrderForm((p) => ({ ...p, device_id: e.target.value }))} fullWidth>
              {devices.map((d) => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
            </TextField>
            <TextField label={`${t('maintenance.orderTitle')} *`} value={orderForm.title}
              onChange={(e) => setOrderForm((p) => ({ ...p, title: e.target.value }))} fullWidth />
            <TextField label={t('maintenance.description')} value={orderForm.description}
              onChange={(e) => setOrderForm((p) => ({ ...p, description: e.target.value }))} fullWidth multiline rows={2} />
            <Stack direction="row" spacing={2}>
              <TextField select label={t('maintenance.priority')} value={orderForm.priority}
                onChange={(e) => setOrderForm((p) => ({ ...p, priority: e.target.value }))} fullWidth>
                {['low', 'normal', 'high', 'urgent'].map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
              </TextField>
              <TextField label={t('maintenance.serviceType')} value={orderForm.service_type}
                onChange={(e) => setOrderForm((p) => ({ ...p, service_type: e.target.value }))} fullWidth
                placeholder="Oil Change, Brake Service..." />
            </Stack>
            {categories.length > 0 && (
              <Stack direction="row" spacing={2}>
                <TextField select label={t('maintenance.addCategory')} value={orderForm.category_id}
                  onChange={(e) => setOrderForm((p) => ({ ...p, category_id: e.target.value }))} fullWidth>
                  <MenuItem value="">{t('common.noData')}</MenuItem>
                  {categories.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                </TextField>
                <TextField select label={t('common.type')} value={orderForm.type_id}
                  onChange={(e) => setOrderForm((p) => ({ ...p, type_id: e.target.value }))} fullWidth>
                  <MenuItem value="">{t('common.noData')}</MenuItem>
                  {types.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
                </TextField>
              </Stack>
            )}
            <Stack direction="row" spacing={2}>
              <TextField label={t('maintenance.odometer')} type="number" value={orderForm.odometer_km}
                onChange={(e) => setOrderForm((p) => ({ ...p, odometer_km: e.target.value }))} fullWidth />
              <TextField label={t('maintenance.cost')} type="number" value={orderForm.cost}
                onChange={(e) => setOrderForm((p) => ({ ...p, cost: e.target.value }))} fullWidth />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label={t('maintenance.vendor')} value={orderForm.vendor_name}
                onChange={(e) => setOrderForm((p) => ({ ...p, vendor_name: e.target.value }))} fullWidth />
              <TextField label={t('maintenance.vendorContact')} value={orderForm.vendor_contact}
                onChange={(e) => setOrderForm((p) => ({ ...p, vendor_contact: e.target.value }))} fullWidth />
            </Stack>
            <TextField label={t('maintenance.notes')} value={orderForm.notes}
              onChange={(e) => setOrderForm((p) => ({ ...p, notes: e.target.value }))} fullWidth multiline rows={2} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOrderDialog(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" disabled={createOrder.isPending || !orderForm.device_id || !orderForm.title}
            onClick={async () => {
              await createOrder.mutateAsync({
                device_id: orderForm.device_id, title: orderForm.title,
                description: orderForm.description, priority: orderForm.priority,
                service_type: orderForm.service_type,
                category_id: orderForm.category_id || undefined,
                type_id: orderForm.type_id || undefined,
                odometer_km: orderForm.odometer_km ? Number(orderForm.odometer_km) : undefined,
                cost: orderForm.cost ? Number(orderForm.cost) : undefined,
                vendor_name: orderForm.vendor_name, vendor_contact: orderForm.vendor_contact,
                notes: orderForm.notes,
              } as any)
              setOrderDialog(false)
            }}>{t('maintenance.newServiceOrder')}</Button>
        </DialogActions>
      </Dialog>

      {/* Report Issue Dialog */}
      <Dialog open={issueDialog} onClose={() => setIssueDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('maintenance.reportIssue')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField select label={`${t('analytics.device')} *`} value={issueForm.device_id}
              onChange={(e) => setIssueForm((p) => ({ ...p, device_id: e.target.value }))} fullWidth>
              {devices.map((d) => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
            </TextField>
            <TextField label={`${t('maintenance.orderTitle')} *`} value={issueForm.title}
              onChange={(e) => setIssueForm((p) => ({ ...p, title: e.target.value }))} fullWidth
              placeholder="Engine light on, AC not working..." />
            <TextField label={t('maintenance.description')} value={issueForm.description}
              onChange={(e) => setIssueForm((p) => ({ ...p, description: e.target.value }))} fullWidth multiline rows={3} />
            <TextField select label={t('alerts.severity')} value={issueForm.severity}
              onChange={(e) => setIssueForm((p) => ({ ...p, severity: e.target.value }))} fullWidth>
              {['low', 'medium', 'high', 'critical'].map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIssueDialog(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" disabled={createIssue.isPending || !issueForm.device_id || !issueForm.title}
            onClick={async () => {
              await createIssue.mutateAsync(issueForm)
              setIssueDialog(false)
              setIssueForm({ device_id: '', title: '', description: '', severity: 'medium' })
            }}>{t('maintenance.reportIssue')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
